<script setup lang="ts" generic="T extends string">

import { useUserStore } from '~/stores/user/useUserStore';

interface PropsTabs {
    tabs: OptionValue<T>[];
    activeTab: T;
    disabledTabs?: T[];
    handlerDisabledTabs?: () => void;
    type?: 'main' | 'local';
}

const props = withDefaults(defineProps<PropsTabs>(), { type: 'main' });
const emit = defineEmits<{
    (e: 'onChange', payload: T): void;
    (e: 'handlerDisabledTabs'): void;
}>();

const userStore = useUserStore();
const tabRefs = ref<Record<string, HTMLElement | null>>({});
const underlineStyle = ref({
    width: '0px',
    left: '0px'
});

const isDisabledWatchList = (tab: OptionValue<T>) => tab.value === 'Watchlist' && !userStore.user;

const updateActiveTab = async (tab: OptionValue<T>) => {
    if (props.disabledTabs && props.disabledTabs.includes(tab.value)) {
        emit('handlerDisabledTabs');
    }
    else if (props.activeTab !== tab.value) {
        emit(
            'onChange',
            tab.value
        );
    }
    await nextTick();
    updateUnderline();
};

const updateUnderline = () => {
    const el = tabRefs.value[props.activeTab];
    if (el) {
        const rect = el.getBoundingClientRect();
        const container = el.offsetParent;
        const containerRect = container?.getBoundingClientRect();
        const scrollLeft = container?.scrollLeft || 0;

        if (containerRect) {
            underlineStyle.value = {
                width: `${rect.width}px`,
                left: `${rect.left - containerRect.left + scrollLeft}px`
            };
        }
    }
};

const setTabRef = (tabValue: T, el: HTMLElement) => {
    tabRefs.value[tabValue] = el;
};

watch(() => props.activeTab, () => {
    nextTick().then(updateUnderline);
});

const tabsContainer = ref<HTMLElement | null>(null);

onMounted(() => {
    updateUnderline();
    if (tabsContainer.value) {
        tabsContainer.value.addEventListener('scroll', updateUnderline);
    }
});

onUnmounted(() => {
    if (tabsContainer.value) {
        tabsContainer.value.removeEventListener('scroll', updateUnderline);
    }
});

</script>

<template>
  <div ref="tabsContainer"
       class="display-flex width-full line gap-40 tabs-container"
       :class="[`async-type-${type}`]"
       v-bind="$attrs"
  >
    <div class="tabs-underline"
         :style="underlineStyle"
    ></div>
    <div v-for="(tab, index) in props.tabs"
         :key="index"
         :ref="el => setTabRef(tab.value, el as HTMLElement)"
         class="display-flex flex-column gap-10 tab-item"
    >
      <button class="tabs-btn compact-500-accent cursor-pointer"
              type="button"
              :aria-label="`${tab.label} tab`"
              v-bind="$attrs"
              :class="['tabs-btn', 'compact-500-accent',
                       { 'tabs-btn--active': tab.value === activeTab },
                       { 'tabs-btn--disabled': isDisabledWatchList(tab) }
              ]"
              @click="updateActiveTab(tab)"
      >
        {{ tab.label }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.async-type {
  &-main {
    margin-bottom: 40px;
  }
}

.tabs-btn {
  padding: 20px 0;
  border: none;
  background: transparent;
  color: var(--content-secondary);
  text-wrap: nowrap;

  &--disabled {
    opacity: 0.7;
  }
}

.tabs-container {
  display: flex;
  position: relative;
  border-bottom: 1px solid var(--border-primary);
  gap: 40px;
  scrollbar-width: none;
  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    max-height: 0;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
}

.tabs-underline {
  position: absolute;
  bottom: 0;
  height: 2px;
  transition: left 0.3s ease, width 0.3s ease;
  background-color: white;
}

.tab-item {
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 10px;
}

.line {
  border-bottom: 1px solid var(--border-primary);
}
</style>
